"use client";

import { useEffect, useRef, useState } from "react";

import { FOOTER_LABEL_WIDTH } from "@demo/libs/panel-layout";

const LABELS_BELOW = FOOTER_LABEL_WIDTH;

/**
 * Whether the footer has room for the toggle labels, from its own width rather
 * than the window's: the prompt column is draggable, so a narrow column on a
 * wide screen has to shed them just as a phone does.
 *
 * Measured rather than a container query because the tooltips change wording
 * with it and render in a portal, outside any container.
 */
export const useFooterLabels = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [showLabels, setShowLabels] = useState(true);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // The border box both times: `contentRect` is the content box, which
    // differs by the footer's padding and would flip the mode with no resize.
    const observer = new ResizeObserver(() => {
      const width = node.getBoundingClientRect().width;
      if (width > 0) setShowLabels(width >= LABELS_BELOW);
    });
    observer.observe(node);

    // The observer's first callback is a frame away.
    const width = node.getBoundingClientRect().width;
    if (width > 0) setShowLabels(width >= LABELS_BELOW);

    return () => observer.disconnect();
  }, []);

  return [ref, showLabels] as const;
};
